import React, { useState, useEffect } from 'react';
import Layout from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function StorageDashboard() {
  const [activeTab, setActiveTab] = useState('buckets'); // 'buckets' or 'volumes'
  const [buckets, setBuckets] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [selectedVolume, setSelectedVolume] = useState(null);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchData = async () => {
    try {
      if (activeTab === 'buckets') {
        const response = await fetch(`${API_URL}/v1/storage/buckets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setBuckets(data.buckets || []);
      } else {
        const [volumesRes, instancesRes] = await Promise.all([
          fetch(`${API_URL}/v1/storage/volumes`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/v1/instances`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        const volumesData = await volumesRes.json();
        const instancesData = await instancesRes.json();
        setVolumes(volumesData.volumes || []);
        setInstances(instancesData.instances || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const createBucket = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/storage/buckets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.get('name'),
          region: formData.get('region')
        })
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Bucket created!\n\nAccess Key: ${data.access_key}\nSecret Key: ${data.secret_key}\nEndpoint: ${data.endpoint}\n\nSave these credentials - they won't be shown again!`);
        setShowCreateModal(false);
        fetchData();
      }
    } catch (error) {
      alert('Error creating bucket: ' + error.message);
    }
  };

  const createVolume = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/storage/volumes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.get('name'),
          size_gb: parseInt(formData.get('size_gb')),
          region: formData.get('region')
        })
      });
      if (response.ok) {
        setShowCreateModal(false);
        fetchData();
      }
    } catch (error) {
      alert('Error creating volume: ' + error.message);
    }
  };

  const attachVolume = async (volumeId, instanceId) => {
    try {
      const response = await fetch(`${API_URL}/v1/storage/volumes/${volumeId}/attach`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ instance_id: instanceId })
      });
      if (response.ok) {
        setShowAttachModal(false);
        fetchData();
      }
    } catch (error) {
      alert('Error attaching volume: ' + error.message);
    }
  };

  const detachVolume = async (volumeId) => {
    if (!window.confirm('Detach this volume?')) return;
    try {
      await fetch(`${API_URL}/v1/storage/volumes/${volumeId}/detach`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error detaching volume: ' + error.message);
    }
  };

  const deleteBucket = async (bucketId) => {
    if (!window.confirm('Delete this bucket? All data will be lost.')) return;
    try {
      await fetch(`${API_URL}/v1/storage/buckets/${bucketId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error deleting bucket: ' + error.message);
    }
  };

  const deleteVolume = async (volumeId) => {
    if (!window.confirm('Delete this volume? All data will be lost.')) return;
    try {
      await fetch(`${API_URL}/v1/storage/volumes/${volumeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error deleting volume: ' + error.message);
    }
  };

  return (
    <Layout userType="customer">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-white mb-6">Storage</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('buckets')}
            className={`px-6 py-3 rounded-lg font-medium ${
              activeTab === 'buckets'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Object Storage
          </button>
          <button
            onClick={() => setActiveTab('volumes')}
            className={`px-6 py-3 rounded-lg font-medium ${
              activeTab === 'volumes'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Block Volumes
          </button>
        </div>

        {/* Create Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="mb-6 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
        >
          + Create {activeTab === 'buckets' ? 'Bucket' : 'Volume'}
        </button>

        {/* Buckets Table */}
        {activeTab === 'buckets' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Region</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Access Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Endpoint</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {buckets.map((bucket) => (
                  <tr key={bucket.id}>
                    <td className="px-6 py-4 text-white font-medium">{bucket.name}</td>
                    <td className="px-6 py-4 text-gray-400">{bucket.region}</td>
                    <td className="px-6 py-4 text-gray-400">{bucket.size_gb.toFixed(2)} GB</td>
                    <td className="px-6 py-4 text-gray-400 font-mono text-sm">{bucket.access_key}</td>
                    <td className="px-6 py-4 text-gray-400 text-sm">{bucket.endpoint}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => deleteBucket(bucket.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {buckets.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No buckets yet. Create your first object storage bucket!
              </div>
            )}
          </div>
        )}

        {/* Volumes Table */}
        {activeTab === 'volumes' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Region</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Attached To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Device</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {volumes.map((volume) => (
                  <tr key={volume.id}>
                    <td className="px-6 py-4 text-white font-medium">{volume.name}</td>
                    <td className="px-6 py-4 text-gray-400">{volume.size_gb} GB</td>
                    <td className="px-6 py-4 text-gray-400">{volume.region}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        volume.status === 'attached' ? 'bg-green-900 text-green-300' :
                        volume.status === 'available' ? 'bg-blue-900 text-blue-300' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {volume.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {volume.instance_id || '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-400 font-mono text-sm">
                      {volume.device_path || '-'}
                    </td>
                    <td className="px-6 py-4 space-x-3">
                      {volume.status === 'available' ? (
                        <>
                          <button
                            onClick={() => {
                              setSelectedVolume(volume);
                              setShowAttachModal(true);
                            }}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            Attach
                          </button>
                          <button
                            onClick={() => deleteVolume(volume.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => detachVolume(volume.id)}
                          className="text-yellow-400 hover:text-yellow-300"
                        >
                          Detach
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {volumes.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No volumes yet. Create your first block storage volume!
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Bucket/Volume Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">
              Create {activeTab === 'buckets' ? 'Bucket' : 'Volume'}
            </h3>
            
            <form onSubmit={activeTab === 'buckets' ? createBucket : createVolume}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck="false"
                    placeholder="my-bucket-name"
                    onChange={(e) => { e.target.value = e.target.value.toLowerCase(); }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers and hyphens only</p>
                </div>

                {activeTab === 'volumes' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Size (GB)</label>
                    <input
                      name="size_gb"
                      type="number"
                      min="1"
                      max="16000"
                      required
                      defaultValue="100"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Region</label>
                  <select
                    name="region"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="us-east">US East</option>
                    <option value="us-west">US West</option>
                    <option value="eu-west">EU West</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attach Volume Modal */}
      {showAttachModal && selectedVolume && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">
              Attach Volume: {selectedVolume.name}
            </h3>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              attachVolume(selectedVolume.id, formData.get('instance_id'));
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">Instance</label>
                <select
                  name="instance_id"
                  required
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                >
                  <option value="">Select instance...</option>
                  {instances.filter(i => i.status === 'running').map(instance => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.region})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAttachModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Attach
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default StorageDashboard;
